import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { MapPin, Search, Loader2 } from 'lucide-react';

/**
 * Formulário de endereço reutilizável.
 * Extraído (sem alterações de comportamento) do cadastro de prestadores
 * (provider/Onboarding.js -> renderStep3). Usado tanto pelo prestador quanto
 * pela etapa "Dados para Assinatura" do cliente.
 *
 * Props:
 *  - value: { cep, street, number, complement, neighborhood, city, state }
 *  - onChange: (novoValue) => void
 */
export const AddressForm = ({ value, onChange }) => {
  const [cepLoading, setCepLoading] = useState(false);
  const [cepValid, setCepValid] = useState(null);

  const handleCepSearch = async () => {
    const cep = (value.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos');
      return;
    }

    setCepLoading(true);
    setCepValid(null);

    try {
      const res = await axios.get(`${API}/utils/validate-cep/${cep}`);
      onChange({
        ...value,
        street: res.data.street || value.street,
        neighborhood: res.data.neighborhood || value.neighborhood,
        city: res.data.city || value.city,
        state: res.data.state || value.state
      });
      setCepValid(true);
      toast.success('CEP encontrado!');
    } catch (error) {
      setCepValid(false);
      toast.error('CEP não encontrado');
    } finally {
      setCepLoading(false);
    }
  };

  return (
    <>
      <div>
        <Label htmlFor="cep">CEP *</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="cep"
              placeholder="00000-000"
              className="pl-10"
              value={value.cep}
              onChange={(e) => {
                onChange({ ...value, cep: e.target.value });
                setCepValid(null);
              }}
              required
              data-testid="cep-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleCepSearch}
            disabled={cepLoading}
            data-testid="cep-search-btn"
          >
            {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
        {cepValid === true && <p className="text-xs text-green-600 mt-1">CEP válido</p>}
        {cepValid === false && <p className="text-xs text-red-500 mt-1">CEP não encontrado</p>}
      </div>

      <div>
        <Label htmlFor="street">Rua / Logradouro *</Label>
        <Input
          id="street"
          placeholder="Av. Paulista"
          value={value.street}
          onChange={(e) => onChange({ ...value, street: e.target.value })}
          required
          data-testid="street-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="number">Número *</Label>
          <Input
            id="number"
            placeholder="1000"
            value={value.number}
            onChange={(e) => onChange({ ...value, number: e.target.value })}
            required
            data-testid="number-input"
          />
        </div>
        <div>
          <Label htmlFor="complement">Complemento</Label>
          <Input
            id="complement"
            placeholder="Sala 01"
            value={value.complement}
            onChange={(e) => onChange({ ...value, complement: e.target.value })}
            data-testid="complement-input"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="neighborhood">Bairro *</Label>
        <Input
          id="neighborhood"
          placeholder="Centro"
          value={value.neighborhood}
          onChange={(e) => onChange({ ...value, neighborhood: e.target.value })}
          required
          data-testid="neighborhood-input"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <Label htmlFor="city">Cidade *</Label>
          <Input
            id="city"
            placeholder="São Paulo"
            value={value.city}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
            required
            data-testid="city-input"
          />
        </div>
        <div>
          <Label htmlFor="state">UF *</Label>
          <Input
            id="state"
            placeholder="SP"
            maxLength={2}
            value={value.state}
            onChange={(e) => onChange({ ...value, state: e.target.value.toUpperCase() })}
            required
            data-testid="state-input"
          />
        </div>
      </div>
    </>
  );
};

export default AddressForm;
